import {
  BulkUpdateFieldType,
  BulkUpdateMLASupplementaryDataModalProps,
  ModalIntroductionProps,
} from "components/BulkUpdateOptionalFieldsModal/types";
import React from "react";
import { BulkUpdateOptionalFieldsModal } from "components/BulkUpdateOptionalFieldsModal/Modal";
import { bulkUpdateMLASupplementaryData } from "actions";
import * as Yup from "yup";
import {
  getCheckboxFieldName,
  ModelNames,
} from "components/BulkUpdateOptionalFieldsModal/util";
import { pluralize } from "lib/pluralize";
import { Tooltip } from "components/Form/FormikControls/Tooltip";
import { Row } from "components/Layout";
import {
  getCurrentSaleSubType,
  getCurrentSpeciesId,
  getMLASupplementaryData,
} from "selectors";
import { selectSaleRoundIdByMLASupplementaryDataSaleLotIdLookup } from "selectors/mlaSupplementaryData";

import { uniq } from "lodash";
import { useSelector } from "react-redux";
import { Species } from "constants/species";
import { isPrimeSaleSubtype, isStoreSaleSubtype } from "lib/sale";
import { resolveValue } from "lib/mlaSupplementaryData";
import {
  allCattle,
  allSheep,
  primeCattle,
  primeSheep,
  storeCattle,
  storeSheep,
} from "./Fields";

const validationSchema = Yup.object().shape({
  [getCheckboxFieldName("fatScore")]: Yup.boolean(),
  fatScore: Yup.number().when(getCheckboxFieldName("fatScore"), {
    is: fatScoreIsChecked =>
      fatScoreIsChecked !== undefined && fatScoreIsChecked === true,
    then: Yup.number()
      .min(0, "Must be greater than 0.")
      .max(5, "Must be less or equal to 5.")
      .typeError("Fat Score must be a number"),
    otherwise: Yup.number().nullable(),
  }),
  [getCheckboxFieldName("muscleScore")]: Yup.boolean(),
  muscleScore: Yup.number().when(getCheckboxFieldName("muscleScore"), {
    is: muscleScoreIsChecked =>
      muscleScoreIsChecked !== undefined && muscleScoreIsChecked === true,
    then: Yup.number()
      .min(0, "Must be greater than 0.")
      .max(6, "Must be less or equal to 6.")
      .typeError("Muscle Score must be a number"),
    otherwise: Yup.number().nullable(),
  }),
});

function ModalIntroduction(props: ModalIntroductionProps): React.JSX.Element {
  const { modelCount } = props;

  return (
    <Row justifyBetween fullWidth paddingHorizontal={3}>
      <div>
        <strong>
          You are bulk editing {modelCount} {pluralize("record", modelCount)}.
          Any changes made to checked fields will be applied to these records.
        </strong>
      </div>
      <Tooltip
        title={
          <>
            All data below relates to supplemental data provided for reporting
            purposes. This information is additive and it will not change
            information on an actual Sale Lot.
            <br />
            For example, if a Sale Lot has an Breed of &quot;Angus&quot; already
            entered, you may override this value for reporting, or clear the
            override in order to revert reporting to show that Breed. Regardless
            of changes made here, the Breed will remain in place on the Sale Lot
          </>
        }
      />
    </Row>
  );
}

function useGetBulkUpdateFields(): BulkUpdateFieldType[] {
  // Fetch the best set of fields for this sale type and species
  // - returning all fields for a species if we can't deduce is a "safe" default, in case
  // that is impperfect, or unset.
  const currentSaleSubType = useSelector(getCurrentSaleSubType);
  const speciesId = useSelector(getCurrentSpeciesId);
  if (speciesId === Species.CATTLE) {
    if (isPrimeSaleSubtype(currentSaleSubType)) {
      return primeCattle;
    }
    if (isStoreSaleSubtype(currentSaleSubType)) {
      return storeCattle;
    }
    return allCattle;
  }
  if (speciesId === Species.SHEEP) {
    if (isPrimeSaleSubtype(currentSaleSubType)) {
      return primeSheep;
    }
    if (isStoreSaleSubtype(currentSaleSubType)) {
      return storeSheep;
    }
    return allSheep;
  }

  // Nothing... won't render well, but what're MLA doing at a Llama sale?
  return [];
}

export function BulkUpdateMLASupplementaryDataModal(
  props: BulkUpdateMLASupplementaryDataModalProps,
): React.JSX.Element {
  const { saleLotIds, onClose, aggridRef } = props;

  const mlaData = useSelector(getMLASupplementaryData);
  const saleRoundIdByMLASupplementaryDataSaleLotIdLookup = useSelector(
    selectSaleRoundIdByMLASupplementaryDataSaleLotIdLookup,
  );

  // Resolve the values for each field of mla data for each sale lot.
  const resolvedValues = Object.keys(mlaData).reduce((acc, mlaId) => {
    const mla = mlaData[mlaId];
    const fieldValues = Object.keys(mla).reduce((fieldAcc, fieldName) => {
      fieldAcc[fieldName] = resolveValue(mla, fieldName, "");
      return fieldAcc;
    }, {});
    acc[mlaId] = fieldValues;
    return acc;
  }, {});

  const uniqueSaleRounds = uniq(
    saleLotIds
      .map(
        saleLotId =>
          saleRoundIdByMLASupplementaryDataSaleLotIdLookup[saleLotId],
      )
      .filter(Boolean),
  );
  // Choose which fields to show based on which sub-type of sale it is.
  const bulkUpdateFields = useGetBulkUpdateFields();
  // Inject a common sale round id, in order to allow reference in the estimated average weight selector
  const initialValues =
    uniqueSaleRounds.length === 1 ? { sale_round_id: uniqueSaleRounds[0] } : {};

  return (
    <BulkUpdateOptionalFieldsModal
      onClose={onClose}
      modelIds={saleLotIds}
      modelName={ModelNames.MLA_DATA}
      bulkUpdateActionCreator={bulkUpdateMLASupplementaryData}
      fields={bulkUpdateFields}
      modelObjects={resolvedValues}
      initialValues={initialValues}
      validationSchema={validationSchema}
      ModalIntroduction={ModalIntroduction}
      aggridRef={aggridRef}
    />
  );
}
